import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';

export interface RegulatorPlannedFormUser {
    user_id: number;
    name: string;
}

export interface RegulatorPlannedFormCompany {
    company_id: number;
    name: string;
}

export interface RegulatorPlannedFormData {
    dateKey: string;
    regulators: RegulatorPlannedFormUser[];
    companies: RegulatorPlannedFormCompany[];
    defaultCompanyId?: number | null;
}

export type RegulatorPlannedFormResult =
    | { mode: 'cancel' }
    | {
          mode: 'save';
          user_ids: number[];
          company_ids: number[];
          date_start: string;
          date_end: string;
          start_time: string;
          end_time: string;
      };

interface CompanyOption extends RegulatorPlannedFormCompany {
    selected: boolean;
}

@Component({
    selector: 'app-regulator-planned-form-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatSelectModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
    ],
    templateUrl: './regulator-planned-form-dialog.component.html',
    styleUrls: ['./regulator-planned-form-dialog.component.scss'],
})
export class RegulatorPlannedFormDialogComponent {
    userIds: number[] = [];
    dateStart: string;
    dateEnd: string;
    timeStart = '08:00';
    timeEnd = '18:00';
    readonly companyOptions: CompanyOption[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: RegulatorPlannedFormData,
        private dialogRef: MatDialogRef<RegulatorPlannedFormDialogComponent, RegulatorPlannedFormResult>,
    ) {
        this.dialogRef.addPanelClass('card-dialog');
        this.dateStart = data.dateKey;
        this.dateEnd = data.dateKey;
        const defaultId = data.defaultCompanyId ?? data.companies[0]?.company_id ?? null;
        this.companyOptions = data.companies.map((c) => ({ ...c, selected: c.company_id === defaultId }));
    }

    get selectedCompanyIds(): number[] {
        return this.companyOptions.filter((c) => c.selected).map((c) => c.company_id);
    }

    get allSelected(): boolean {
        return this.companyOptions.length > 0 && this.companyOptions.every((c) => c.selected);
    }

    get someSelected(): boolean {
        return this.companyOptions.some((c) => c.selected) && !this.allSelected;
    }

    toggleAll(checked: boolean): void {
        for (const c of this.companyOptions) c.selected = checked;
    }

    get canSave(): boolean {
        return (
            this.userIds.length > 0 &&
            this.selectedCompanyIds.length > 0 &&
            !!this.dateStart &&
            !!this.dateEnd &&
            this.dateEnd >= this.dateStart &&
            !!this.timeStart &&
            !!this.timeEnd
        );
    }

    cancel(): void {
        this.dialogRef.close({ mode: 'cancel' });
    }

    submit(): void {
        if (!this.canSave) return;
        this.dialogRef.close({
            mode: 'save',
            user_ids: this.userIds,
            company_ids: this.selectedCompanyIds,
            date_start: this.dateStart,
            date_end: this.dateEnd,
            start_time: this.timeStart,
            end_time: this.timeEnd,
        });
    }
}
